import { ComponentPropsWithoutRef, useEffect, useLayoutEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { Layer } from './styles'

type Props = ComponentPropsWithoutRef<'div'>

const FADE_OUT_MS = 250

// The backdrops showing right now: the first one locks the page, the last one closing frees it
let openBackdropCount = 0

// Only iPhone browsers (Safari, Chrome and Firefox alike, all on WebKit) keep a see-through toolbar at the
// bottom of the window that the page paints under. Android and desktop browsers keep opaque toolbars outside
// the page, so there the window is the whole screen
const isIphone = /iPhone|iPod/.test(navigator.userAgent)

function getScreenReach() {
  if (!isIphone) {
    return window.innerHeight
  }

  // iOS reports the screen size in portrait whatever the orientation, so landscape takes the short side
  const isLandscape = window.innerWidth > window.innerHeight
  const screenHeight = isLandscape
    ? Math.min(window.screen.width, window.screen.height)
    : Math.max(window.screen.width, window.screen.height)

  return Math.max(window.innerHeight, screenHeight)
}

// The dimmed page behind a modal. Part of the document rather than a fixed layer: iOS 26 Safari clips fixed
// elements to its layout viewport, which stops short of the bottom toolbar, while the document keeps painting
// under it. So it is laid over the current scroll position, on iPhones sized to the whole screen so the dim
// runs under the toolbar, and the page is locked while it shows. The web menu's category picker does the same.
// Rendered under body, so no positioned ancestor can catch it
export default function Backdrop({ className, style, ...rest }: Props) {
  const isOpen = className === 'open'
  const [placement, setPlacement] = useState({ top: 0, height: 0 })

  useLayoutEffect(() => {
    if (!isOpen) {
      return
    }

    const place = () => setPlacement({ top: window.scrollY, height: getScreenReach() })

    place()
    // Should something still move the page (a scroll in flight when the modal opened), the dim follows it
    window.addEventListener('scroll', place, { passive: true })
    window.addEventListener('resize', place)

    openBackdropCount += 1
    document.body.style.overflow = 'hidden'

    return () => {
      window.removeEventListener('scroll', place)
      window.removeEventListener('resize', place)

      openBackdropCount -= 1
      if (openBackdropCount === 0) {
        document.body.style.overflow = ''
      }
    }
  }, [isOpen])

  // Closed, it fades out where it was and only then stops stretching the page
  useEffect(() => {
    if (isOpen) {
      return
    }

    const timer = window.setTimeout(
      () => setPlacement(current => (current.height ? { top: 0, height: 0 } : current)),
      FADE_OUT_MS
    )

    return () => window.clearTimeout(timer)
  }, [isOpen])

  return createPortal(
    <Layer {...rest} className={className} style={{ ...style, top: placement.top, height: placement.height }} />,
    document.body
  )
}
